import { useState } from "react";
import { useNavigate, useLocation } from 'react-router-dom';

// ─── NAV DATA (shared by desktop sidebar + mobile drawer + bottom nav) ──────

const navItems = [
  { section: "OVERVIEW",  items: [{ icon: "ti-layout-dashboard", label: "Dashboard",     path: "/dashboard"     }] },
  { section: "ENGAGE",    items: [
      { icon: "ti-robot",        label: "Nigella VOX",   path: "/ai-agent"      },
      { icon: "ti-message-dots", label: "Conversations", path: "/conversations" },
      { icon: "ti-users",        label: "Leads",         path: "/leads"         },
  ]},
  { section: "CONFIGURE", items: [
      { icon: "ti-book",     label: "Knowledge Base", path: "/knowledge" },
      { icon: "ti-plug",     label: "Channels",       path: "/channels"  },
      { icon: "ti-settings", label: "Settings",       path: "/settings"  },
  ]},
];

// ─── DESKTOP SIDEBAR (unchanged look, now hidden on mobile) ──────────────────

function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  
  return (
    <aside className="hidden lg:flex flex-col h-full border-r flex-shrink-0"
      style={{ width: "15%", minWidth: 180, background: "#161412", borderColor: "#2a2825" }}
    >
      <div className="flex items-center gap-3 px-5 py-5 border-b" style={{ borderColor: "#2a2825" }}>
        <div className="w-9 h-9 bg-orange-600 rounded-xl flex items-center justify-center flex-shrink-0">
          <i className="ti ti-bolt text-white text-base" />
        </div>
        <div>
          <div className="text-sm font-bold text-stone-100 leading-tight">Nigella VOX</div>
          <div className="text-[9px] text-stone-500 uppercase tracking-widest">Agent Console</div>
        </div>
      </div>

      <nav className="flex flex-col gap-5 px-3 py-5 flex-1 overflow-y-auto">
        {navItems.map(({ section, items }) => (
          <div key={section}>
            <div className="text-[9px] text-stone-600 uppercase tracking-widest mb-2 px-2">{section}</div>
            {items.map(({ icon, label, path }) => {
              const active = location.pathname === path;
              return (
                <div
                  key={label}
                  onClick={() => navigate(path)}
                  className={`flex items-center gap-3 px-3 py-2 rounded-xl cursor-pointer mb-0.5 transition-all ${
                    active ? "bg-orange-600/20 text-orange-400 border border-orange-600/30"
                          : "text-stone-400 hover:text-stone-200 hover:bg-white/5"
                  }`}
                >
                  <i className={`ti ${icon} text-base flex-shrink-0`} />
                  <span className="text-xs font-medium">{label}</span>
                </div>
              );
            })}
          </div>
        ))}
      </nav>

      <div className="px-5 pb-5 border-t pt-4" style={{ borderColor: "#2a2825" }}>
        <div className="flex items-center gap-1.5 text-[9px] text-stone-600 mb-1">
          <i className="ti ti-shield-check text-orange-700" />
          Secured workspace
        </div>
        <div className="text-[9px] text-stone-700">
          © 2026{" "}
          <a
            href="https://awktechz.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-orange-500 transition-colors"
          >
            AWK Technologies
          </a>
        </div>
      </div>
    </aside>
  );
}

// ─── MOBILE DRAWER MENU ──────────────────────────────────────────────────────

function MobileDrawer({ open, onClose }) {
  const navigate = useNavigate();
  const location = useLocation();
  
  if (!open) return null;
  return (
    <>
      <div className="fixed inset-0 z-40 lg:hidden" style={{ background: "rgba(0,0,0,0.7)" }} onClick={onClose} />
      <div className="fixed top-0 left-0 bottom-0 z-50 flex flex-col lg:hidden"
        style={{ width: 260, background: "#161412", borderRight: "1px solid #2a2825" }}>
        <div className="flex items-center justify-between px-5 py-5 border-b" style={{ borderColor: "#2a2825" }}>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-orange-600 rounded-xl flex items-center justify-center flex-shrink-0">
              <i className="ti ti-bolt text-white text-base" />
            </div>
            <div>
              <div className="text-sm font-bold text-stone-100 leading-tight">Nigella VOX</div>
              <div className="text-[9px] text-stone-500 uppercase tracking-widest">Agent Console</div>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-xl flex items-center justify-center text-stone-500" style={{ background: "#252320" }}>
            <i className="ti ti-x text-sm" />
          </button>
        </div>

        <nav className="flex flex-col gap-5 px-3 py-5 flex-1 overflow-y-auto">
          {navItems.map(({ section, items }) => (
            <div key={section}>
              <div className="text-[9px] text-stone-600 uppercase tracking-widest mb-2 px-2">{section}</div>
              {items.map(({ icon, label, path }) => {
                const active = location.pathname === path;
                return (
                  <div
                    key={label}
                    onClick={() => { navigate(path); onClose(); }}
                    className={`flex items-center gap-3 px-3 py-3 rounded-xl cursor-pointer mb-0.5 transition-all ${
                      active ? "bg-orange-600/20 text-orange-400 border border-orange-600/30"
                             : "text-stone-400 hover:text-stone-200 hover:bg-white/5"
                    }`}
                  >
                    <i className={`ti ${icon} text-base flex-shrink-0`} />
                    <span className="text-sm font-medium">{label}</span>
                  </div>
                );
              })}
            </div>
          ))}
        </nav>

        <div className="px-5 pb-6 border-t pt-4" style={{ borderColor: "#2a2825" }}>
          <div className="flex items-center gap-1.5 text-[9px] text-stone-600 mb-1">
            <i className="ti ti-shield-check text-orange-700" />
            Secured workspace
          </div>
          <div className="text-[9px] text-stone-700">
            © 2026{" "}
            <a
              href="https://awktechz.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-orange-500 transition-colors"
            >
              AWK Technologies
            </a>
          </div>
        </div>
      </div>
    </>
  );
}

// ─── MOBILE BOTTOM NAV ───────────────────────────────────────────────────────

function MobileBottomNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const items = [
    { icon: "ti-layout-dashboard", label: "Home",  path: "/dashboard"     },
    { icon: "ti-robot",            label: "VOX",   path: "/ai-agent"      },
    { icon: "ti-message-dots",     label: "Chats", path: "/conversations" },
    { icon: "ti-users",            label: "Leads", path: "/leads"         },
    { icon: "ti-book",             label: "KB",    path: "/knowledge"     },
  ];
  return (
    <nav className="fixed bottom-0 left-0 right-0 z-30 flex items-center justify-around border-t lg:hidden"
      style={{ background: "#161412", borderColor: "#2a2825", paddingBottom: "env(safe-area-inset-bottom)" }}>
      {items.map(({ icon, label, path }) => {
        const active = location.pathname === path;
        return (
          <button key={path} onClick={() => navigate(path)}
            className="flex flex-col items-center gap-0.5 py-2.5 px-3 flex-1 transition-all"
            style={{ color: active ? "#e8602a" : "#6b6560" }}>
            <i className={`ti ${icon} text-lg`} />
            <span style={{ fontSize: 9, fontWeight: active ? 700 : 500 }}>{label}</span>
          </button>
        );
      })}
    </nav>
  );
}

// ─── TOPBAR ───────────────────────────────────────────────────────────────────
function Topbar({ onLogout, onMenuOpen }) {
  const [showLogoutDropdown, setShowLogoutDropdown] = useState(false);

  return (
    <header className="flex items-center gap-3 px-4 lg:px-6 py-3 border-b flex-shrink-0"
      style={{ background: "#161412", borderColor: "#2a2825" }}
    >
      {/* Hamburger — mobile only */}
      <button onClick={onMenuOpen}
        className="lg:hidden w-8 h-8 rounded-xl flex items-center justify-center text-white/70 flex-shrink-0"
        style={{ background: "#242220" }}>
        <i className="ti ti-menu-2 text-base" />
      </button>

      <div className="hidden lg:flex gap-1.5">
        {["ti-moon-stars", "ti-sun"].map((ic) => (
          <button key={ic}
            className="w-7 h-7 rounded-full flex items-center justify-center text-white/60 hover:text-white transition-colors"
            style={{ background: "#242220" }}
          >
            <i className={`ti ${ic} text-xs`} />
          </button>
        ))}
      </div>

      <div className="flex-1 flex items-center gap-2 rounded-full px-3 lg:px-4 py-2 border lg:mx-4"
        style={{ background: "#1e1c1a", borderColor: "#333" }}
      >
        <i className="ti ti-search text-white/50 text-sm" />
        <span className="text-sm text-white/50 hidden sm:block">Search channels…</span>
        <span className="text-sm text-white/50 sm:hidden">Search…</span>
      </div>

      <div className="flex items-center gap-2 ml-auto">
        {/* Logout button with dropdown */}
        <div className="relative">
          <button
            onMouseEnter={() => setShowLogoutDropdown(true)}
            onMouseLeave={() => setShowLogoutDropdown(false)}
            onClick={onLogout}
            className="w-9 h-9 rounded-xl flex items-center justify-center text-white hover:bg-orange-700 transition-all duration-200 shadow-lg"
            style={{ background: "#e8602a" }}
            title="Logout"
          >
            <i className="ti ti-power text-lg" />
          </button>

          {/* Dropdown on hover */}
          {showLogoutDropdown && (
            <div
              onMouseEnter={() => setShowLogoutDropdown(true)}
              onMouseLeave={() => setShowLogoutDropdown(false)}
              className="absolute right-0 top-full mt-1 w-32 rounded-xl border shadow-2xl py-1 z-50"
              style={{ background: "#1a1815", borderColor: "#2a2825" }}
            >
              <button
                onClick={onLogout}
                className="w-full px-4 py-2.5 text-left text-sm text-white hover:bg-orange-500/10 hover:text-orange-400 transition-colors flex items-center gap-2"
              >
                <i className="ti ti-logout text-orange-500" />
                <span className="font-medium">Logout</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
// ─── DATA ─────────────────────────────────────────────────────────────────────
const channels = [
  {
    id: 1, name: "WhatsApp", key: "whatsapp",
    icon: "ti-brand-whatsapp", iconBg: "linear-gradient(135deg,#25d366,#128c7e)",
    status: "Disconnected", statusColor: "#6b7280", statusBg: "#1a1a1a",
    description: "WhatsApp Business API integration. Connect to receive and send messages automatically.",
    stats: { conversations: 0, leads: 0, responseTime: "—", uptime: "—" },
    lastActivity: "Never", agent: "—",
    tags: ["Not Connected"],
    config: {},
  },
  {
    id: 2, name: "Facebook", key: "facebook", 
    icon: "ti-brand-facebook", iconBg: "linear-gradient(135deg,#1877f2,#0e4cb8)",
    status: "Disconnected", statusColor: "#6b7280", statusBg: "#1a1a1a",
    description: "Facebook Messenger integration for automated customer support and engagement.",
    stats: { conversations: 0, leads: 0, responseTime: "—", uptime: "—" },
    lastActivity: "Never", agent: "—",
    tags: ["Not Connected"],
    config: {},
  },
  {
    id: 3, name: "Instagram", key: "instagram",
    icon: "ti-brand-instagram", iconBg: "linear-gradient(135deg,#e1306c,#833ab4)",
    status: "Disconnected", statusColor: "#6b7280", statusBg: "#1a1a1a",
    description: "Instagram DM automation. Respond to story replies and direct messages automatically.",
    stats: { conversations: 0, leads: 0, responseTime: "—", uptime: "—" },
    lastActivity: "Never", agent: "—",
    tags: ["Not Connected"],
    config: {},
  },
];

const statCards = [
  { label: "Connected",       value: "0",    icon: "ti-circle-check",   color: "#34c97c" },
  { label: "Total Messages",  value: "0", icon: "ti-message-dots",   color: "#e8602a" },
  { label: "Leads Captured",  value: "0",icon: "ti-users",          color: "#5b8dee" },
  { label: "Avg Response",    value: "—", icon: "ti-clock",          color: "#a855f7" },
];

// ─── STAT BAR ─────────────────────────────────────────────────────────────────
function StatBar() {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
      {statCards.map(({ label, value, icon, color }) => (
        <div key={label} className="flex items-center gap-3 rounded-2xl px-3 lg:px-4 py-3 border"
          style={{ background: "#1a1815", borderColor: "#2a2825" }}
        >
          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: `${color}18`, border: `1px solid ${color}30` }}
          >
            <i className={`ti ${icon} text-lg`} style={{ color }} />
          </div>
          <div className="min-w-0">
            <div className="text-2xl font-extrabold text-white leading-tight">{value}</div>
            <div className="text-xs text-white/60 mt-0.5 truncate">{label}</div>
          </div>
        </div>
      ))}
    </div>
  );
}
// ─── CHANNEL CARD ─────────────────────────────────────────────────────────────
function ChannelCard({ channel, onClick, selected }) {
  const { name, icon, iconBg, status, statusColor, statusBg, description, stats, lastActivity, tags } = channel;
  return (
    <div
      onClick={() => onClick(channel)}
      className="flex flex-col gap-3 rounded-2xl p-4 border cursor-pointer transition-all hover:border-orange-600/30"
      style={{
        background: selected ? "#e8602a08" : "#1a1815",
        borderColor: selected ? "#e8602a40" : "#2a2825",
        borderLeft: selected ? "2px solid #e8602a" : "2px solid transparent",
      }}
    >
      {/* Top row */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: iconBg }}
          >
            <i className={`ti ${icon} text-lg text-white`} />
          </div>
          <div>
            <div className="text-base font-bold text-white">{name}</div>
            <div className="text-xs text-white/60">{lastActivity}</div>
          </div>
        </div>
        <span className="text-xs font-semibold rounded-full px-2.5 py-1"
          style={{ color: statusColor, background: statusBg }}
        >
          {status}
        </span>
      </div>

      {/* Description */}
      <p className="text-sm text-white/70 leading-relaxed line-clamp-2">{description}</p>

      {/* Stats mini row */}
      <div className="grid grid-cols-3 gap-2">
        {[
          { label: "Chats",    val: stats.conversations },
          { label: "Leads",    val: stats.leads },
          { label: "Response", val: stats.responseTime },
        ].map(({ label, val }) => (
          <div key={label} className="rounded-xl px-2.5 py-2 text-center" style={{ background: "#252320" }}>
            <div className="text-sm font-bold text-white">{val}</div>
            <div className="text-xs text-white/60 mt-0.5">{label}</div>
          </div>
        ))}
      </div>

      {/* Tags */}
      <div className="flex items-center justify-between">
        <div className="flex gap-1.5 flex-wrap">
          {tags.map((tag) => (
            <span key={tag} className="text-xs text-white/60 rounded-full px-2 py-0.5"
              style={{ background: "#252320" }}
            >
              {tag}
            </span>
          ))}
        </div>
        <i className="ti ti-chevron-right text-white/50 text-sm" />
      </div>
    </div>
  );
}
// ─── CHANNEL DETAIL PANEL ─────────────────────────────────────────────────────
function ChannelDetailPanel({ channel, onClose }) {
  const [activeTab, setActiveTab] = useState("Overview");
  if (!channel) return null;

  const tabs = ["Overview", "Config", "Activity"];
  const activityLog = [
    { icon: "ti-message-dots", text: "124 conversations handled by AI today",  time: "Today" },
    { icon: "ti-users",        text: "38 new leads captured automatically",     time: "Today" },
    { icon: "ti-alert-circle", text: "3 conversations escalated to human agent",time: "Yesterday" },
    { icon: "ti-refresh",      text: "Channel reconnected after brief timeout", time: "2 days ago" },
    { icon: "ti-sparkles",     text: "AI model updated to Nigella Vox v2.1",    time: "1 week ago" },
  ];

  const isConnected = channel.status === "Connected";
  const isPaused    = channel.status === "Paused";

  const Content = (
    <>
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b flex-shrink-0"
        style={{ borderColor: "#2a2825" }}
      >
        <span className="text-sm font-semibold text-white">Channel Details</span>
        <button onClick={onClose}
          className="w-6 h-6 rounded-lg flex items-center justify-center text-white/60 hover:text-white"
          style={{ background: "#252320" }}
        >
          <i className="ti ti-x text-sm" />
        </button>
      </div>

      <div className="flex flex-col gap-4 p-5">
        {/* Icon + name */}
        <div className="flex flex-col items-center gap-2 py-2">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0"
            style={{ background: channel.iconBg }}
          >
            <i className={`ti ${channel.icon} text-2xl text-white`} />
          </div>
          <div className="text-center">
            <div className="text-base font-bold text-white">{channel.name}</div>
            <div className="text-xs text-white/60 mt-0.5">{channel.description.slice(0, 60)}…</div>
          </div>
          <span className="text-xs font-semibold rounded-full px-3 py-1"
            style={{ color: channel.statusColor, background: channel.statusBg }}
          >
            {channel.status}
          </span>
        </div>

        {/* Tabs */}
        <div className="flex rounded-xl overflow-hidden border" style={{ borderColor: "#2a2825" }}>
          {tabs.map((t) => (
            <button key={t} onClick={() => setActiveTab(t)}
              className={`flex-1 py-2 text-xs font-semibold transition-all ${
                activeTab === t ? "text-orange-400" : "text-white/60 hover:text-white"
              }`}
              style={activeTab === t ? { background: "#e8602a18" } : { background: "#1a1815" }}
            >
              {t}
            </button>
          ))}
        </div>
        {/* ── OVERVIEW TAB ── */}
        {activeTab === "Overview" && (
          <>
            <div className="grid grid-cols-2 gap-2">
              {[
                { label: "Conversations", val: channel.stats.conversations, icon: "ti-message-dots", color: "#5b8dee" },
                { label: "Leads",         val: channel.stats.leads,         icon: "ti-users",        color: "#34c97c" },
                { label: "Response Time", val: channel.stats.responseTime,  icon: "ti-clock",        color: "#f59e0b" },
                { label: "Uptime",        val: channel.stats.uptime,        icon: "ti-activity",     color: "#a855f7" },
              ].map(({ label, val, icon, color }) => (
                <div key={label} className="rounded-xl p-3 border" style={{ background: "#1a1815", borderColor: "#2a2825" }}>
                  <div className="flex items-center gap-1.5 mb-1">
                    <i className={`ti ${icon} text-xs`} style={{ color }} />
                    <span className="text-xs text-white/60 uppercase tracking-wider">{label}</span>
                  </div>
                  <div className="text-base font-extrabold text-white">{val}</div>
                </div>
              ))}
            </div>
            <div className="rounded-xl p-3 border" style={{ background: "#1a1815", borderColor: "#2a2825" }}>
              <div className="text-xs text-white/60 uppercase tracking-wider mb-1">AI Agent</div>
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-lg flex items-center justify-center"
                  style={{ background: "#e8602a22" }}
                >
                  <i className="ti ti-robot text-orange-500 text-xs" />
                </div>
                <span className="text-sm font-semibold text-white">{channel.agent}</span>
              </div>
            </div>
            <div className="rounded-xl p-3 border" style={{ background: "#1a1815", borderColor: "#2a2825" }}>
              <div className="text-xs text-white/60 uppercase tracking-wider mb-1">Last Activity</div>
              <div className="text-sm text-white">{channel.lastActivity}</div>
            </div>
          </>
        )}

        {/* ── CONFIG TAB ── */}
        {activeTab === "Config" && (
          <>
            {Object.keys(channel.config).length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 gap-2">
                <i className="ti ti-plug-off text-2xl text-white/40" />
                <div className="text-sm text-white/60 text-center">No configuration yet.<br/>Connect this channel first.</div>
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                {Object.entries(channel.config).map(([key, val]) => (
                  <div key={key} className="flex items-start justify-between gap-2 rounded-xl px-3 py-2.5 border"
                    style={{ background: "#1a1815", borderColor: "#2a2825" }}
                  >
                    <div className="text-xs text-white/60 uppercase tracking-wider capitalize flex-shrink-0">
                      {key.replace(/([A-Z])/g, " $1")}
                    </div>
                    <div className="text-sm text-white text-right truncate max-w-[60%]">{val}</div>
                  </div>
                ))}
              </div>
            )}
            {Object.keys(channel.config).length > 0 && (
              <button className="w-full py-2.5 rounded-xl text-sm font-medium text-white/70 border hover:text-white transition-colors"
                style={{ background: "#1a1815", borderColor: "#2a2825" }}
              >
                <i className="ti ti-edit text-sm mr-1.5" />
                Edit Configuration
              </button>
            )}
          </>
        )}
        {/* ── ACTIVITY TAB ── */}
        {activeTab === "Activity" && (
          <div className="flex flex-col gap-0">
            {activityLog.map((item, i) => (
              <div key={i} className="flex gap-3 pb-4 relative">
                {i < activityLog.length - 1 && (
                  <div className="absolute left-[14px] top-7 bottom-0 w-px" style={{ background: "#2a2825" }} />
                )}
                <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 z-10"
                  style={{ background: "#252320", border: "1px solid #2a2825" }}
                >
                  <i className={`ti ${item.icon} text-xs text-orange-500`} />
                </div>
                <div className="pt-0.5 min-w-0">
                  <div className="text-sm text-white/80 leading-snug">{item.text}</div>
                  <div className="text-xs text-white/50 mt-0.5">{item.time}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-col gap-2 mt-2">
          {isConnected && (
            <>
              <button className="w-full py-2.5 rounded-xl text-sm font-semibold text-white hover:opacity-90 transition-opacity"
                style={{ background: "#e8602a" }}
              >
                <i className="ti ti-settings text-sm mr-1.5" />
                Manage Channel
              </button>
              <button className="w-full py-2.5 rounded-xl text-sm font-medium text-amber-400 border hover:opacity-80 transition-opacity"
                style={{ background: "#2d201080", borderColor: "#f59e0b40" }}
              >
                <i className="ti ti-player-pause text-sm mr-1.5" />
                Pause Channel
              </button>
            </>
          )}

          {isPaused && (
            <>
              <button className="w-full py-2.5 rounded-xl text-sm font-semibold text-white hover:opacity-90 transition-opacity"
                style={{ background: "#34c97c" }}
              >
                <i className="ti ti-player-play text-sm mr-1.5" />
                Resume Channel
              </button>
              <button className="w-full py-2.5 rounded-xl text-sm font-medium text-white/70 border hover:text-white transition-colors"
                style={{ background: "#1a1815", borderColor: "#2a2825" }}
              >
                <i className="ti ti-settings text-sm mr-1.5" />
                Configure
              </button>
            </>
          )}

          {!isConnected && !isPaused && (
            <button className="w-full py-2.5 rounded-xl text-sm font-semibold text-white hover:opacity-90 transition-opacity"
              style={{ background: "#e8602a" }}
            >
              <i className="ti ti-plug text-sm mr-1.5" />
              Connect Channel
            </button>
          )}

          <button className="w-full py-2.5 rounded-xl text-sm font-medium text-white/60 border hover:text-white transition-colors"
            style={{ background: "#1a1815", borderColor: "#2a2825" }}
          >
            <i className="ti ti-chart-bar text-sm mr-1.5" />
            View Analytics
          </button>
        </div>
      </div>
    </>
  );

  return (
    <>
      {/* Desktop — always-visible right rail */}
      <div className="hidden lg:flex flex-col border-l flex-shrink-0 overflow-y-auto"
        style={{ width: 300, background: "#161412", borderColor: "#2a2825" }}
      >
        {Content}
      </div>

      {/* Mobile — slide-over drawer */}
      <div className="lg:hidden fixed inset-0 z-50 flex justify-end">
        <div className="absolute inset-0" style={{ background: "rgba(0,0,0,0.65)" }} onClick={onClose} />
        <div className="relative flex flex-col h-full w-[85%] max-w-[320px] overflow-y-auto"
          style={{ background: "#161412" }}>
          {Content}
        </div>
      </div>
    </>
  );
}
// ─── CONNECT FORM ─────────────────────────────────────────────────────────────
function ConnectForm({ onClose }) {
  const [formData, setFormData] = useState({
    metaAccessToken: "",
    appSecret: "",
    webhookVerifyToken: "",
    whatsappPhoneId: "",
    instagramId: "",
    facebookPageId: ""
  });

  const handleSubmit = () => {
    // Handle form submission
    console.log("Connecting with:", formData);
    onClose();
  };

  return (
    <div className="flex flex-col lg:flex-row flex-1 min-h-0 overflow-y-auto lg:overflow-hidden">
      {/* Left side - Form */}
      <div className="flex flex-col flex-1 min-w-0 p-4 lg:p-6" style={{ background: "#111010" }}>
        <div className="max-w-2xl mx-auto w-full h-full lg:overflow-y-auto" style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}>
          <style jsx>{`
            div::-webkit-scrollbar {
              display: none;
            }
          `}</style>
          
          {/* Header */}
          <div className="mb-6 lg:mb-8">
            <div className="flex items-center gap-3 mb-4">
              <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center text-stone-500 hover:text-stone-300 flex-shrink-0"
                style={{ background: "#252320" }}
              >
                <i className="ti ti-arrow-left text-sm" />
              </button>
              <h1 className="text-lg lg:text-xl font-extrabold text-stone-100">Connect channels</h1>
            </div>
            <p className="text-sm text-stone-500">Link your Meta channels to bring the AI agent online</p>
          </div>

          {/* Meta Connection Section */}
          <div className="rounded-2xl border p-4 lg:p-6 mb-6" style={{ background: "#1a1815", borderColor: "#2a2825" }}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: "#e8602a" }}>
                <i className="ti ti-brand-meta text-white text-sm" />
              </div>
              <div>
                <h2 className="text-lg lg:text-xl font-bold text-white">Meta connection</h2>
                <p className="text-sm lg:text-base text-white/60">Connect with the official Meta Embedded Signup, or enter credentials manually.</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-white">Meta access token</label>
                <input
                  type="text"
                  placeholder="EAAG..."
                  value={formData.metaAccessToken}
                  onChange={(e) => setFormData({...formData, metaAccessToken: e.target.value})}
                  className="w-full px-3 py-2.5 rounded-xl border text-sm text-white placeholder-white/40 outline-none focus:border-orange-500/50 transition-colors"
                  style={{ background: "#252320", borderColor: "#333" }}
                />
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-white">App secret</label>
                <input
                  type="password"
                  placeholder="app_secret"
                  value={formData.appSecret}
                  onChange={(e) => setFormData({...formData, appSecret: e.target.value})}
                  className="w-full px-3 py-2.5 rounded-xl border text-sm text-white placeholder-white/40 outline-none focus:border-orange-500/50 transition-colors"
                  style={{ background: "#252320", borderColor: "#333" }}
                />
              </div>

              <div className="flex flex-col gap-2 md:col-span-2">
                <label className="text-sm font-medium text-white">Webhook verify token</label>
                <input
                  type="text"
                  placeholder="3E6AF64B1CF4542D90C6D120"
                  value={formData.webhookVerifyToken}
                  onChange={(e) => setFormData({...formData, webhookVerifyToken: e.target.value})}
                  className="w-full px-3 py-2.5 rounded-xl border text-sm text-white placeholder-white/40 outline-none focus:border-orange-500/50 transition-colors"
                  style={{ background: "#252320", borderColor: "#333" }}
                />
              </div>
            </div>

            {/* Channel IDs */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <i className="ti ti-brand-whatsapp text-green-500 text-base" />
                  <label className="text-sm font-medium text-white">WhatsApp phone id</label>
                </div>
                <input
                  type="text"
                  placeholder="102938..."
                  value={formData.whatsappPhoneId}
                  onChange={(e) => setFormData({...formData, whatsappPhoneId: e.target.value})}
                  className="w-full px-3 py-2.5 rounded-xl border text-sm text-white placeholder-white/40 outline-none focus:border-orange-500/50 transition-colors"
                  style={{ background: "#252320", borderColor: "#333" }}
                />
              </div>

              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <i className="ti ti-brand-instagram text-pink-500 text-base" />
                  <label className="text-sm font-medium text-white">Instagram id</label>
                </div>
                <input
                  type="text"
                  placeholder="178..."
                  value={formData.instagramId}
                  onChange={(e) => setFormData({...formData, instagramId: e.target.value})}
                  className="w-full px-3 py-2.5 rounded-xl border text-sm text-white placeholder-white/40 outline-none focus:border-orange-500/50 transition-colors"
                  style={{ background: "#252320", borderColor: "#333" }}
                />
              </div>

              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <i className="ti ti-brand-facebook text-blue-500 text-base" />
                  <label className="text-sm font-medium text-white">Facebook page id</label>
                </div>
                <input
                  type="text"
                  placeholder="page id"
                  value={formData.facebookPageId}
                  onChange={(e) => setFormData({...formData, facebookPageId: e.target.value})}
                  className="w-full px-3 py-2.5 rounded-xl border text-sm text-white placeholder-white/40 outline-none focus:border-orange-500/50 transition-colors"
                  style={{ background: "#252320", borderColor: "#333" }}
                />
              </div>
            </div>

            {/* Connect Button */}
            <button
              onClick={handleSubmit}
              className="w-full mt-6 py-3 rounded-xl text-sm font-semibold text-white hover:opacity-90 transition-opacity"
              style={{ background: "#e8602a" }}
            >
              🔗 Connect & go live
            </button>
          </div>
        </div>
      </div>

      {/* Right side - How it works */}
      <div className="w-full lg:w-80 flex-shrink-0 p-4 lg:p-6" style={{ background: "#111010" }}>
        <div className="rounded-2xl p-5 lg:p-6" style={{ background: "#1a1815", border: "1px solid #2a2825" }}>
          <h3 className="text-xl font-bold text-white mb-4">How it works</h3>
          
          <div className="space-y-4">
            <div className="flex gap-3">
              <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: "#e8602a" }}>
                <span className="text-white text-sm font-bold">1</span>
              </div>
              <div>
                <h4 className="text-base font-semibold text-white">Connect Meta</h4>
                <p className="text-sm text-white/60 mt-1">Authorize WhatsApp, Instagram or Messenger.</p>
              </div>
            </div>

            <div className="flex gap-3">
              <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: "#e8602a" }}>
                <span className="text-white text-sm font-bold">2</span>
              </div>
              <div>
                <h4 className="text-base font-semibold text-white">Add knowledge</h4>
                <p className="text-sm text-white/60 mt-1">Tell the AI your hours, pricing & products.</p>
              </div>
            </div>

            <div className="flex gap-3">
              <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: "#e8602a" }}>
                <span className="text-white text-sm font-bold">3</span>
              </div>
              <div>
                <h4 className="text-base font-semibold text-white">Start replying</h4>
                <p className="text-sm text-white/60 mt-1">Customers message you — the AI answers 24/7.</p>
              </div>
            </div>
          </div>

          <div className="mt-6 pt-4 border-t" style={{ borderColor: "#2a2825" }}>
            <div className="flex items-center gap-2 text-sm">
              <div className="w-2 h-2 rounded-full bg-amber-500"></div>
              <span className="text-white/60">Status: </span>
              <span className="text-amber-400 font-medium">Pending setup</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
// ─── MAIN CONTENT ─────────────────────────────────────────────────────────────
function ChannelsContent() {
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [filter, setFilter] = useState("All");

  const filterTabs = ["All", "Disconnected"];
  const filtered = channels.filter((c) =>
    filter === "All" ? true : c.status === filter);

  if (showForm) {
    return <ConnectForm onClose={() => setShowForm(false)} />;
  }

  return (
    <div className="flex flex-1 min-h-0 overflow-hidden">
      <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
        <div className="flex-1 overflow-y-auto p-4 lg:p-5 pb-24 lg:pb-5">
          {/* Page header */}
          <div className="flex flex-col sm:flex-row sm:items-center items-start justify-between gap-3 mb-5">
            <div>
              <h1 className="text-2xl font-extrabold text-white leading-tight">Channels</h1>
              <p className="text-sm text-white/60 mt-0.5">Connect and manage every platform your AI agent operates on.</p>
            </div>
            <button
              onClick={() => setShowForm(true)}
              className="flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold text-white hover:opacity-90 transition-opacity w-full sm:w-auto flex-shrink-0"
              style={{ background: "#e8602a" }}
            >
              <i className="ti ti-plus text-base" />
              Connect Channel
            </button>
          </div>

          {/* Stats */}
          <StatBar />

          {/* Filter tabs */}
          <div className="flex items-center gap-2 mb-5 overflow-x-auto">
            {filterTabs.map((tab) => (
              <button key={tab} onClick={() => setFilter(tab)}
                className="text-[11px] font-medium rounded-full px-4 py-2 border transition-all flex-shrink-0"
                style={filter === tab
                  ? { background: "#e8602a22", color: "#e8602a", borderColor: "#e8602a50" }
                  : { background: "#1a1815", color: "#6b7280", borderColor: "transparent" }}
              >
                {tab}
                <span className="ml-1.5 text-[9px] rounded-full px-1.5 py-0.5"
                  style={{ background: "#252320", color: "#6b7280" }}
                >
                  {tab === "All" ? channels.length : channels.filter((c) => c.status === tab).length}
                </span>
              </button>
            ))}
          </div>

          {/* Channels grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {filtered.map((channel) => (
              <ChannelCard
                key={channel.id}
                channel={channel}
                selected={selectedChannel?.id === channel.id}
                onClick={(c) => setSelectedChannel(selectedChannel?.id === c.id ? null : c)}
              />
            ))}
          </div>

          {filtered.length === 0 && (
            <div className="flex flex-col items-center justify-center py-20 gap-3">
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center"
                style={{ background: "#1a1815", border: "1px solid #2a2825" }}
              >
                <i className="ti ti-plug-off text-2xl text-white/50" />
              </div>
              <div className="text-base font-semibold text-white/70">No channels in this category</div>
            </div>
          )}
        </div>
      </div>

      {/* Detail panel */}
      {selectedChannel && (
        <ChannelDetailPanel channel={selectedChannel} onClose={() => setSelectedChannel(null)} />
      )}
    </div>
  );
}

// ─── ROOT ─────────────────────────────────────────────────────────────────────
export default function Channels({ onLogout }) {
  const [drawerOpen, setDrawerOpen] = useState(false);

  return (
    <div className="flex h-screen w-screen overflow-hidden" style={{ background: "#111010" }}>
      {/* Desktop sidebar */}
      <Sidebar />

      {/* Mobile drawer */}
      <MobileDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />

      <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
        <Topbar onLogout={onLogout} onMenuOpen={() => setDrawerOpen(true)} />
        <ChannelsContent />
      </div>

      {/* Mobile bottom nav */}
      <MobileBottomNav  />
    </div>
  );
}
